import { lazy, useContext } from 'react';
import { Route, Routes } from 'react-router-dom';

import { useHasSettingsEnabled } from '~/lib/navigationPermissions';
import { useUser } from '~/modules/auth/hooks/useUser';
import { BrandContext } from '~/modules/branding';
import { LinkFarmRouter } from '~/modules/link-farm/router';
import { RemoteRouter } from '~/modules/remotes/router';

import { ErrorPage } from './components/ErrorPage';
import { App as RosterApp } from './modules/artist-roster/App/App';
import { App as DdexConsoleApp } from './modules/ddex-console/App';
import { App as LabelPerfApp } from './modules/label-perf/App';
import { CatalogApp } from './modules/label-perf/catalog/CatalogApp';
import { App as MarketingApp } from './modules/marketing/App/App';
import { App as ProfileApp } from './modules/my-profile/App';
import { PrimaryLayout } from './modules/navigation/components/PrimaryLayout';
import { App as SettingsApp } from './modules/settings/App';

const UserManagementApp = lazy(
  () => import('./modules/user-management/App/App'),
);

/**
 * Top-level router combining module-level routers
 */
function Router() {
  const brand = useContext(BrandContext);
  const { features } = useUser();
  const { hasPermission } = useUser();
  const canAccessSettings =
    useHasSettingsEnabled(features) && hasPermission('USER.VIEW');

  const rootRoute =
    brand.name === 'WMG' ? <LinkFarmRouter /> : <RemoteRouter />;

  return (
    <Routes>
      <Route element={<PrimaryLayout />}>
        <Route path="*" element={rootRoute} />
        <Route path="catalog/*" element={<CatalogApp />} />
        <Route path="label/*" element={<LabelPerfApp />} />
        <Route path="marketing/*" element={<MarketingApp />} />
        <Route path="release/ddex/*" element={<DdexConsoleApp />} />
        <Route path="roster/*" element={<RosterApp />} />
        <Route path="users/*" element={<UserManagementApp />} />

        <Route
          path="settings/*"
          element={
            canAccessSettings ? (
              <SettingsApp />
            ) : (
              <ErrorPage type="noAccessPage" />
            )
          }
        />

        {canAccessSettings ? (
          <Route path="my-profile-2/*" element={<ProfileApp />} />
        ) : null}
      </Route>
    </Routes>
  );
}

Router.displayName = 'Router';

export { Router };
